import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

import { counselSpeechUrl } from '@/features/counsel'

// back が返す音声の形 (16-bit・mono・little endian の PCM。読み上げのモデルの出力そのまま)
const SAMPLE_RATE = 24000

// 席の輪の倍率 (design-book 04-apps/executive-rag.md 相手の席)。録音ボタンの useAudioPulse の倍率
// (× 5.0 / 上限 0.2) はマイクの小さな入力向けで、読み上げの声に当てると 9 割のフレームで上限に張り付く
const SEAT_PULSE = {
  scale: [0.08, 0.06],
  light: [0.9, 0.55],
} as const

const SPEECH_FAILED = '読み上げられませんでした。もう一度押してください。'

export type SpeechPhase = 'busy' | 'playing'

interface Playing {
  index: number
  analyser: AnalyserNode
  sources: AudioBufferSourceNode[]
  abort: AbortController
  stopRing: () => void
  timer: number
}

// 声の強弱を輪に出す (useAudioPulse と同じ均し方・呼吸で、倍率だけが違う)。返すのは止める関数
const pulseRing = (analyser: AnalyserNode, ring: HTMLElement) => {
  const buffer = new Uint8Array(analyser.frequencyBinCount)
  let smoothed = 0
  let phase = 0
  let raf = 0
  const tick = () => {
    analyser.getByteFrequencyData(buffer)
    let sum = 0
    for (let i = 0; i < buffer.length; i++) sum += buffer[i]
    smoothed = smoothed * 0.6 + (sum / buffer.length / 255) * 0.4
    phase += 0.05
    const breathe = 0.02 * (0.5 + 0.5 * Math.sin(phase))
    const grow = Math.min(smoothed * SEAT_PULSE.scale[0], SEAT_PULSE.scale[1])
    ring.style.transform = `scale(${1 + breathe + grow})`
    ring.style.opacity = `${
      0.2 + Math.min(smoothed * SEAT_PULSE.light[0], SEAT_PULSE.light[1])
    }`
    raf = requestAnimationFrame(tick)
  }
  tick()
  return () => {
    cancelAnimationFrame(raf)
    ring.style.transform = ''
    ring.style.opacity = ''
  }
}

/**
 * nagi の行の読み上げ。押した行の返事を、届いた分から鳴らす (design-book 04-apps/kakeru.md がんの相談)。
 *
 * - 音の器 (AudioContext) はページに 1 つ。**利用者の操作の中で起こす** (`wake`) — iOS の Safari は
 *   操作の外で作った器を鳴らさない。自動の読み上げは、送る操作の数秒後に返事が届いてから鳴る。
 * - 鳴っている間は席の輪 (`ringRef`) が声の強弱で動く。
 * - 同時に鳴るのは 1 行だけ。別の行を読むと、鳴っている方を止めてから読む。
 */
export const useSpeech = (token: string, ringRef: RefObject<HTMLElement>) => {
  const contextRef = useRef<AudioContext | null>(null)
  const playingRef = useRef<Playing | null>(null)
  const [speaking, setSpeaking] = useState<{
    index: number
    phase: SpeechPhase
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const wake = useCallback(() => {
    if (!contextRef.current) {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext
      contextRef.current = new AudioCtx()
    }
    if (contextRef.current.state === 'suspended') {
      void contextRef.current.resume()
    }
    return contextRef.current
  }, [])

  const hush = useCallback(() => {
    const playing = playingRef.current
    if (!playing) return
    playingRef.current = null
    playing.abort.abort()
    playing.stopRing()
    window.clearTimeout(playing.timer)
    playing.sources.forEach((source) => {
      try {
        source.stop()
      } catch {
        // まだ始まっていない塊は止められない (鳴らないまま捨てる)
      }
    })
    playing.analyser.disconnect()
    setSpeaking(null)
  }, [])

  // sessionId は呼ぶ時に渡す。最初の返事は、届いた同じ処理の中で会話の id を知る
  const speak = useCallback(
    (index: number, sessionId: string | null) => {
      hush()
      setError(null)
      const context = wake()
      const analyser = context.createAnalyser()
      analyser.fftSize = 128
      analyser.connect(context.destination)
      const playing: Playing = {
        index,
        analyser,
        sources: [],
        abort: new AbortController(),
        stopRing: () => undefined,
        timer: 0,
      }
      playingRef.current = playing
      setSpeaking({ index, phase: 'busy' })

      let at = 0
      let carry: Uint8Array | null = null
      // 塊は 2 バイトの境で切れているとは限らない。端の 1 バイトは次の塊に回す
      const play = (chunk: Uint8Array) => {
        let bytes = chunk
        if (carry) {
          const joined = new Uint8Array(carry.length + bytes.length)
          joined.set(carry)
          joined.set(bytes, carry.length)
          bytes = joined
          carry = null
        }
        if (bytes.length % 2) {
          carry = bytes.slice(-1)
          bytes = bytes.subarray(0, bytes.length - 1)
        }
        if (!bytes.length) return
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.length)
        const frames = bytes.length / 2
        const buffer = context.createBuffer(1, frames, SAMPLE_RATE)
        const channel = buffer.getChannelData(0)
        for (let i = 0; i < frames; i++) {
          channel[i] = view.getInt16(i * 2, true) / 32768
        }
        const source = context.createBufferSource()
        source.buffer = buffer
        source.connect(analyser)
        playing.sources.push(source)
        // 塊を隙間なく後ろに継ぐ。届くのが遅れた塊だけは届いた時から鳴らす
        at = Math.max(at, context.currentTime + 0.05)
        source.start(at)
        at += buffer.duration
        if (playing.sources.length === 1) {
          setSpeaking({ index, phase: 'playing' })
          if (ringRef.current) {
            playing.stopRing = pulseRing(analyser, ringRef.current)
          }
        }
      }

      fetch(counselSpeechUrl(token, index, sessionId), {
        credentials: 'include',
        signal: playing.abort.signal,
      })
        .then(async (response) => {
          if (!response.ok || !response.body) throw new Error('speech')
          const reader = response.body.getReader()
          for (;;) {
            const { done, value } = await reader.read()
            if (playingRef.current !== playing || done) return
            play(value)
          }
        })
        .then(() => {
          if (playingRef.current !== playing) return
          // 最後の塊が鳴り終わったら戻す
          playing.timer = window.setTimeout(() => {
            if (playingRef.current === playing) hush()
          }, Math.max(0, at - context.currentTime) * 1000 + 100)
        })
        .catch(() => {
          if (playingRef.current !== playing) return // 止めた (abort) なら何も言わない
          hush()
          setError(SPEECH_FAILED)
        })
    },
    [hush, ringRef, token, wake]
  )

  // 鳴っている行を押すと止まる。別の行を押すと、そちらを読む
  const toggle = useCallback(
    (index: number, sessionId: string | null) => {
      if (playingRef.current?.index === index) hush()
      else speak(index, sessionId)
    },
    [hush, speak]
  )

  useEffect(
    () => () => {
      hush()
      void contextRef.current?.close()
    },
    [hush]
  )

  return {
    speaking,
    error,
    wake,
    speak,
    toggle,
    hush,
  }
}

const AUTO_SPEAK_KEY = 'counsel-auto-speak'

// 自動で読み上げの入り切り。既定は切 (待合や電車で開く)。状態は端末ごとに覚える
export const useAutoSpeak = () => {
  const [isOn, setIsOn] = useState(() => {
    try {
      return localStorage.getItem(AUTO_SPEAK_KEY) === '1'
    } catch {
      return false
    }
  })
  const flip = useCallback(() => {
    setIsOn((prev) => {
      try {
        localStorage.setItem(AUTO_SPEAK_KEY, prev ? '0' : '1')
      } catch {
        // 覚えられなくても、このページの間は効く
      }
      return !prev
    })
  }, [])
  return [isOn, flip] as const
}
